// The AR canonical-rest-pose placement math (ADR-0035 §5 / §7), extracted from the
// spike's `bakePillow` into a pure, GL-free function so it can be unit-tested in
// isolation — the mirror of `cushionRestPose` for the in-room bake.
//
// Given the pillow mesh's (un-scaled) bounding box and the physical size it should
// be placed at, it returns the uniform scale + translation that:
//   • scale the model so its longest PRINTED FACE side (max of width/height — the
//     thickness along Z is irrelevant to the real-world footprint, sizeModel.ts)
//     hits the requested cm, at true metre-scale (the exporters treat one scene
//     unit as one metre: USDZ metersPerUnit = 1; glTF is metres by convention);
//   • centre the model on X and Z;
//   • rest its lowest point on the ground plane (y = 0), so AR Quick Look / Scene
//     Viewer plant it ON the surface, not half-buried or floating.
//
// It encodes the decision from the prototype: scale = targetLongestCm / 100 /
// max(size.x, size.y); then translate by (-center.x, -min.y, -center.z) in the
// scaled (metre) frame. Transient camera state (pan/orbit/zoom) is deliberately
// NOT an input — the OS AR engine re-places the object, so the bake carries only
// the object itself in a canonical pose (ADR-0035 §7).

// A minimal 3-component vector. three's `Vector3` (and `Box3.min` / `Box3.max`)
// structurally satisfy this, so the bake passes them straight in — but the module
// itself imports nothing, so it unit-tests without a WebGL/three scene.
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// The mesh's axis-aligned bounding box in its own (un-scaled) units — exactly a
// three `Box3`.
export interface ArBounds {
  min: Vec3;
  max: Vec3;
}

export interface ArPlacement {
  // Uniform scale applied to the mesh so its longest face side = targetLongestCm.
  scale: number;
  // Translation applied AFTER the scale, in the scaled (metre) frame — centres the
  // model on X/Z and rests its lowest point on y = 0. Assign straight to the
  // exported group's `position` (the group carries the scale).
  translation: Vec3;
  // The exported model's physical size in metres (for the on-screen readout + the
  // bake's sanity assertions). dims = size × scale.
  dimsMeters: Vec3;
}

// Uniform scale so the longest FACE side (X or Y) reaches the target cm, in metres.
// A degenerate (zero-extent) box falls back to a longest side of 1 so the scale is
// finite rather than NaN/Infinity.
export function computeArPlacement(bounds: ArBounds, targetLongestCm: number): ArPlacement {
  const size: Vec3 = {
    x: bounds.max.x - bounds.min.x,
    y: bounds.max.y - bounds.min.y,
    z: bounds.max.z - bounds.min.z,
  };
  const longestFace = Math.max(size.x, size.y) || 1;
  const scale = targetLongestCm / 100 / longestFace;

  const center: Vec3 = {
    x: (bounds.min.x + bounds.max.x) / 2,
    y: (bounds.min.y + bounds.max.y) / 2,
    z: (bounds.min.z + bounds.max.z) / 2,
  };

  // In the scaled frame every coordinate is multiplied by `scale`, so the offsets
  // that recentre X/Z and drop the bottom to the plane are the scaled centre / min.
  return {
    scale,
    translation: {
      x: -center.x * scale,
      y: -bounds.min.y * scale,
      z: -center.z * scale,
    },
    dimsMeters: {
      x: size.x * scale,
      y: size.y * scale,
      z: size.z * scale,
    },
  };
}
